import React, { useEffect, useState } from "react";
import {
  MDBTabsPane,
  MDBRow,
  MDBCol,
  MDBTable,
  MDBTableBody,
  MDBContainer,
} from "mdb-react-ui-kit";
import { format, parseISO } from "date-fns";
import ArrowProgressBar from "../ArrowProgressBar/ArrowProgressBar";
import axios from "axios";
import DrafterInfo from "../Info/DrafterInfo/DrafterInfo";
import RequestorInfo from "../Info/RequestorInfo/RequestorInfo";
import ByRequestOfInfo from "../Info/ByRequestOfInfo/ByRequestOfInfo";
import DeadlineCategory from "../Info/DeadlineCategory/DeadlineCategoryName";
import CommitteeName from "../Info/Committee/CommitteeName";

const convertISOToReadableTimeFormat = (date) =>
  format(parseISO(date), "MM/dd/yy");

const convertTimestamp = (timestamp) => {
  try {
    if (!timestamp) return "";
    const datePart = timestamp.split(" ")[0];
    const [year, month, day] = datePart.split("-");
    const formattedMonth = month.padStart(2, "0");
    const formattedDay = day.padStart(2, "0");
    return `${formattedMonth}/${formattedDay}/${year}`;
  } catch (error) {
    return timestamp;
  }
};

const getSponsor = async (sponsorId) => {
  const response = await axios.post(
    `${process.env.REACT_APP_LAWS_BACKEND_HOST}/legislators/v1/legislators/search?limit=100&offset=0`,
    { legislatorIds: [sponsorId] }
  );
  const fetchedLegislators = response?.data?.content[0] || [];
  return fetchedLegislators;
};

const getMatchingNumber = (status, billType) => {
  const house = ["20", "40", "60", "90", "110", "180"]; // HOUSE FLOW
  const senate = ["20", "40", "60", "90", "110", "180"]; // SENATE FLOW
  let steps = 0;
  let dates = [];

  // Sort the status array by timeStamp
  const sortedStatus = status.sort(
    (a, b) => new Date(b.timeStamp) - new Date(a.timeStamp)
  );

  sortedStatus.forEach((statusItem) => {
    if (statusItem.billStatusCode.billProgressCategory) {
      let statusBillType;
      statusBillType = statusItem.billStatusCode.chamber;
      const billType = statusBillType[0];
      const code =
        statusItem.billStatusCode.billProgressCategory.code.toString();
      if (billType === "H" && house.includes(code)) {
        const index = house.indexOf(code) + 1;
        steps = Math.max(steps, index);
        dates.push(convertISOToReadableTimeFormat(statusItem.timeStamp));
      } else if (billType === "S" && senate.includes(code)) {
        const index = senate.indexOf(code) + 1;
        steps = Math.max(steps, index);
        dates.push(convertISOToReadableTimeFormat(statusItem.timeStamp));
      }
    }
  });
  return { steps, dates };
};

const Summary = ({
  active,
  bill,
  onClickOpenTab,
  ordinal,
  legislatureOrdinal,
}) => {
  const params = new URLSearchParams();
  params.append("legislatureOrdinal", `${legislatureOrdinal}`);
  params.append("sessionOrdinal", `${ordinal}`);
  params.append("billType", `${bill.billType.code}`);
  params.append("billNumber", `${bill.billNumber}`);

  const primarySponsor = bill.sponsorId;
  const BillTypeChar = bill.billType
    ? bill.billType.code.charAt(0).toUpperCase()
    : "H";
  const { steps, dates } = getMatchingNumber(
    bill.draft.billStatuses,
    BillTypeChar
  );

  const [billType, setBillType] = useState(BillTypeChar);
  const [step, setStep] = useState(steps);
  const [legislatorProfilePic, setLegislatorProfilePic] = useState(false);
  const [legislatorProfileLink, setLegislatorProfileLink] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [billSection, setBillSection] = useState([]);
  const [chapterPdfUrl, setChapterPdfUrl] = useState();
  const [hasAmendments, setHasAmendments] = useState(false);

  const [billSponsor, setBillSponsor] = useState();

  // Directly access billStatuses array
  const billStatuses = bill.draft.billStatuses;

  // Sort billStatuses by timeStamp in ascending order
  const sortDateWithAscendingStatus = [...billStatuses].sort(
    (a, b) => new Date(b.timeStamp) - new Date(a.timeStamp)
  );

  useEffect(() => {
    async function fetchProfile() {
      let legislator;
      legislator = await getSponsor(primarySponsor);
      setBillSponsor(legislator);
      if (legislator.emailAddress) {
        try {
          const { data } = await axios.get(
            `${process.env.REACT_APP_LEGISLATOR_PHOTO_HOST}/v1/legislators/findByEmail?email=${legislator.emailAddress}`
          );
          setLegislatorProfilePic(data?.photo.url);
          setLegislatorProfileLink(data?.links?.profileUrl);
        } catch (error) {
          console.error("Error fetching legislator data:", error);
        }
      }
    }

    fetchProfile();
  }, []);

  function padNumber(number, prefix, suffix, length) {
    return `${prefix}${number.toString().padStart(length, "0")}.${suffix}`;
  }

  async function fetchSingleChapter() {
    try {
      if(!bill.sessionLawChapterNumber) {
        return null;
      }

      if (!ordinal || !bill?.sessionId) {
        throw new Error("Missing required parameters: ordinal or sessionId");
      }

      const encodedRootFolder = encodeURIComponent(
        `${legislatureOrdinal}/${ordinal}/Bills/Chapters`
      );
      const folderResponse = await axios.get(
        `${process.env.REACT_APP_DOCS_BACKEND_HOST}/v1/folders/findByPath?path=${encodedRootFolder}`
      );

      if (!folderResponse.data || !folderResponse.data.id) {
        throw new Error("Invalid folder id supplied");
      }

      const chapterDirectoryResponse = folderResponse.data;

      const archiveChapterDoc = padNumber(
        bill.sessionLawChapterNumber
          ? bill.sessionLawChapterNumber.toString()
          : "",
        "CH",
        "pdf",
        4
      );

      const documentResponse = await axios.get(
        `${process.env.REACT_APP_LAWS_BACKEND_HOST}/docs/v1/documents/listByFolderFileName?folderId=${chapterDirectoryResponse.id}&fileName=${archiveChapterDoc}`
      );

      if (!documentResponse.data) {
        throw new Error("No data received from document response");
      }

      return documentResponse.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error(
          "Axios error fetching chapters:",
          error.response?.data || error.message
        );
      } else {
        console.error("Error fetching chapters:", error.message);
      }
      return null;
    }
  }

  async function fetchBillSection() {
    try {
      const response = await axios.get(`${process.env.REACT_APP_LAWS_BACKEND_HOST}/bills/v1/sessionLawEffectiveDates/findByBillId?billId=${bill.id}`);
      setBillSection(response.data);
    } catch (error) {
      console.error("Error fetching bill section: ", error);
    }
  }

  async function fetchChapterPdfUrl(chapterId) {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_LAWS_BACKEND_HOST}/docs/v1/documents/longPdfUrl?documentId=${chapterId}`
      );
      setChapterPdfUrl(response.data);
    } catch (error) {
      console.error("Error fetching chapter PDF URL: ", error);
    }
  }

  async function checkAmendments() {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_LAWS_BACKEND_HOST}/docs/v1/documents/getBillAmendments?${params.toString()}`
      );
      setHasAmendments(response.data && response.data.length > 0);
    } catch (error) {
      console.error("Error checking amendments: ", error);
      setHasAmendments(false);
    }
  }

  useEffect(() => {
    async function fetchAll() {
      setIsLoading(true);
      try {
        await Promise.all([
          fetchBillSection(),
          checkAmendments()
        ]);
        const chapterPdfDoc = await fetchSingleChapter();
        if (chapterPdfDoc) {
          await fetchChapterPdfUrl(chapterPdfDoc[0].id);
        }
      } catch (error) {
        console.error("Error fetching all data: ", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchAll();
  }, []);

  return (
    <MDBTabsPane show={active}>
      <MDBContainer fluid>
        <ArrowProgressBar step={step} billType={billType} dates={dates} />
        <MDBRow className="g-2">
          <MDBCol
            show={active.toString()}
            md="3"
            className="pe-0 order-last order-md-first"
          >
            {isLoading && legislatorProfilePic ? (
              <div className="text-center m-5">
                <div
                  className="spinner-border"
                  style={{ width: "2rem", height: "2rem" }}
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <div className="text-center mt-2">
                <figure>
                  <img
                    src={
                      legislatorProfilePic ||
                      `${process.env.PUBLIC_URL}/primary-sponsor-not-found.png`
                    }
                    className="figure-img img-fluid rounded-circle mb-3"
                    alt={"Legislator Portrait"}
                  />
                  {billSponsor ? (
                    <figcaption className="figure-caption">
                      <h5 className="mb-0">
                        <div>Sponsor:</div>
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          href={legislatorProfileLink || undefined}
                        >
                          {billSponsor.firstName + " " + billSponsor.lastName}
                        </a>
                      </h5>
                      (
                      {billSponsor.politicalParty?.code
                        ? billSponsor.politicalParty?.code
                        : "Party code not available"}
                      ){" "}
                      {billType ? (billType.startsWith("H") ? "HD" : "SD") : ""}{" "}
                      {billSponsor.district?.number
                        ? billSponsor.district?.number
                        : "District number not available"}
                    </figcaption>
                  ) : (
                    <figcaption className="figure-caption">
                      Primary sponsor unavailable
                    </figcaption>
                  )}
                </figure>
              </div>
            )}
          </MDBCol>
          <MDBCol md="9" className="ps-0">
            <MDBTable small>
              <MDBTableBody>
                <tr>
                  <th scope="row" width="225">
                    Draft #
                  </th>
                  <td>
                    {bill.draft.draftNumber == null
                      ? "No Draft number"
                      : bill.draft.draftNumber}
                  </td>
                </tr>
                <tr>
                  <th scope="row" width="225">
                    Bill #
                  </th>
                  <td>
                    {bill.billType.code == null ? "" : bill.billType.code}{" "}
                    {bill.billNumber == null ? "" : bill.billNumber}
                  </td>
                </tr>
                <tr>
                  <th scope="row">Chapter #</th>
                  <td>
                    {chapterPdfUrl ? (
                      <a
                        href={chapterPdfUrl || undefined}
                        className="text-decoration-underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {bill.sessionLawChapterNumber}
                      </a>
                    ) : (
                      bill.sessionLawChapterNumber || "None"
                    )}
                  </td>
                </tr>
                {hasAmendments && (
                  <tr>
                    <th scope="row">Amendment(s)</th>
                    <td>
                      <a className="text-decoration-underline" onClick={(e) => { e.preventDefault(); onClickOpenTab('Amendments') }} href="#" target="_blank" rel="noopener noreferrer">Yes</a>
                    </td>
                  </tr>
                )}
                <tr>
                  <th scope="row">Current Status</th>
                  <td>
                    {sortDateWithAscendingStatus[0]?.standingCommitteeId ? (
                      <CommitteeName
                        committeeId={
                          sortDateWithAscendingStatus[0].standingCommitteeId
                        }
                        legislatureId={ordinal}
                      />
                    ) : sortDateWithAscendingStatus[0].billStatusCode.name ? (
                      sortDateWithAscendingStatus[0].billStatusCode.name
                    ) : (
                      "Status not available"
                    )}
                  </td>
                </tr>

                {/*
                  <tr>
                  <th scope="row">Co-Sponsor(s)</th>
                  <td>{bill.coSponsors == null ? "None" : "Mark"}</td>
                </tr>
                */}
                <tr>
                  <th scope="row">Drafter</th>
                  <td>
                    {bill.drafterStaffMemberId ? (
                      <span>
                        <DrafterInfo drafterId={bill.drafterStaffMemberId} />
                      </span>
                    ) : (
                      ""
                    )}
                  </td>
                </tr>
                <tr>
                  <th scope="row">Requester</th>
                  <td>
                    {bill.draft.requesterId ? (
                      <span>
                        <RequestorInfo
                          requestorId={bill.draft.requesterId}
                          requestorType={bill.draft.requesterType}
                        />
                      </span>
                    ) : (
                      ""
                    )}
                  </td>
                </tr>
                {bill.draft.byRequestOfs.length > 0 && (
                  <tr>
                    <th scope="row">By Request Of</th>
                    <td>
                      <span>
                        <ByRequestOfInfo requestors={bill.draft.byRequestOfs} />
                      </span>
                    </td>
                  </tr>
                )}
                {/*<tr>
                    <th scope="row">Fiscal Note</th>
                    <td>
                        {isLoading ? (
                            <span>Loading...</span>
                        ) : (
                            <span>{hasFiscalNotes ? <a href="#" className="text-decoration-underline" onClick={(e) => {
                                e.preventDefault();
                                onClickOpenTab('Fiscal')
                            }}>Yes</a> : "No"}</span>
                        )}
                    </td>
                </tr>*/}
                {bill.draft.fiscalNote === "Y" && (
                  <tr>
                    <th scope="row">Fiscal Analyst</th>
                    <td>&nbsp;</td>
                  </tr>
                )}

                <tr>
                  <th scope="row">Category</th>
                  <td>
                    <DeadlineCategory deadlineCategoryId={bill.deadlineCodeId} sessionId={bill.sessionId} renderDescription={true} />
                  </td>
                </tr>
                <tr>
                  <th scope="row">Subject(s)</th>
                  <td>
                    {bill.draft.subjects.map((subject, index) => (
                      <span key={index}>
                        {subject.subjectCode.description}
                        {index < bill.draft.subjects.length - 1 && ", "}
                      </span>
                    ))}
                  </td>
                </tr>
                <tr>
                  <th scope="row">Transmittal Date</th>
                  <td><DeadlineCategory deadlineCategoryId={bill.deadlineCodeId} sessionId={bill.sessionId} renderTransmittalDate={true} /></td>
                </tr>
                <tr>
                  <th scope="row">Return Date</th>
                  <td><DeadlineCategory deadlineCategoryId={bill.deadlineCodeId} sessionId={bill.sessionId} renderReturnOffsetDay={true} /></td>
                </tr>
                {billSection && billSection.length > 0 && (
                <tr>
                  <td colSpan="2">
                    <table className="table table-bordered mt-2 mb-0">
                      <thead className="table-light">
                        <tr>
                          <th scope="col">Sections Affected</th>
                          <th scope="col">Effective Date</th>
                          <th scope="col">Qualifier</th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          billSection?.map((item) => {
                            return <tr>
                              <td>{item.sectionDescription}</td>
                              <td>{convertTimestamp(item.effectiveDate)}</td>
                              <td>{item.effectiveDateQualifier}</td>
                            </tr>
                          })
                        }
                      </tbody>
                    </table>
                  </td>
                </tr>
                )}
              </MDBTableBody>
            </MDBTable>
          </MDBCol>
        </MDBRow>
      </MDBContainer>
    </MDBTabsPane>
  );
};

export default Summary;
